/**
 * src/components/shared/ToastProvider.jsx
 *
 * Premium iWork-style toast notification system for AC Nautitech.
 * Replaces native alert() everywhere — see CLAUDE.md "Interactive Element Rules"
 * (Alerts: Never use alert(). Use toast/notification components consistent
 * with the app aesthetic.)
 *
 * Usage:
 *   import { useToast } from '@/components/shared/ToastProvider';
 *   const { showError, showSuccess, showWarning, showInfo, showToast } = useToast();
 *   showError('Upload failed: network error');
 *
 * API:
 *   showToast({ severity, message, durationMs? })
 *   showSuccess(message, opts?)  -> auto-dismiss 4s
 *   showInfo(message, opts?)     -> auto-dismiss 4s
 *   showWarning(message, opts?)  -> auto-dismiss 5s
 *   showError(message, opts?)    -> auto-dismiss 5s (8s if opts.critical)
 *
 * Behavior:
 *   - Stacks max 4 simultaneous; queue overflow drops oldest visible.
 *   - Position: top-center, fixed, above all app chrome.
 *   - Click toast or dismiss button to remove early.
 *   - aria-live="polite" for accessibility.
 *   - 44px touch target on dismiss button.
 *   - Severity styling uses correct token pairing per CLAUDE.md color rules:
 *       success -> bg var(--ok-tint)   + color var(--ok)
 *       error   -> bg var(--bad-tint)  + color var(--bad)
 *       warning -> bg var(--warn-tint) + color var(--warn)
 *       info    -> bg var(--info-tint) + color var(--info)
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext.jsx';

const ToastContext = createContext(null);

const MAX_VISIBLE = 4;
const DEFAULT_DURATION = {
  success: 4000,
  info: 4000,
  warning: 5000,
  error: 5000,
};

const SEVERITY_STYLE = {
  success: { bg: 'var(--ok-tint)',   color: 'var(--ok)',   Icon: CheckCircle2 },
  info:    { bg: 'var(--info-tint)', color: 'var(--info)', Icon: Info },
  warning: { bg: 'var(--warn-tint)', color: 'var(--warn)', Icon: AlertTriangle },
  error:   { bg: 'var(--bad-tint)',  color: 'var(--bad)',  Icon: AlertCircle },
};

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) { clearTimeout(tm); timers.current.delete(id); }
  }, []);

  const showToast = useCallback((opts) => {
    const severity = SEVERITY_STYLE[opts?.severity] ? opts.severity : 'info';
    const message = String(opts?.message ?? '');
    const durationMs = typeof opts?.durationMs === 'number'
      ? opts.durationMs
      : (opts?.critical ? 8000 : DEFAULT_DURATION[severity]);
    const id = nextId++;
    const action = opts?.action && typeof opts.action.onClick === 'function'
      ? { label: String(opts.action.label || 'View'), onClick: opts.action.onClick }
      : null;
    setToasts((prev) => {
      const next = [...prev, { id, severity, message, action }];
      // Drop oldest if exceeding visible cap
      while (next.length > MAX_VISIBLE) {
        const dropped = next.shift();
        const tm = timers.current.get(dropped.id);
        if (tm) { clearTimeout(tm); timers.current.delete(dropped.id); }
      }
      return next;
    });
    if (durationMs > 0) {
      const tm = setTimeout(() => dismiss(id), durationMs);
      timers.current.set(id, tm);
    }
    return id;
  }, [dismiss]);

  const showSuccess = useCallback((message, opts) => showToast({ severity: 'success', message, ...opts }), [showToast]);
  const showInfo    = useCallback((message, opts) => showToast({ severity: 'info',    message, ...opts }), [showToast]);
  const showWarning = useCallback((message, opts) => showToast({ severity: 'warning', message, ...opts }), [showToast]);
  const showError   = useCallback((message, opts) => showToast({ severity: 'error',   message, ...opts }), [showToast]);

  // Cleanup on unmount
  useEffect(() => () => {
    for (const tm of timers.current.values()) clearTimeout(tm);
    timers.current.clear();
  }, []);

  const value = useMemo(() => ({
    showToast, showSuccess, showInfo, showWarning, showError, dismiss,
  }), [showToast, showSuccess, showInfo, showWarning, showError, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }) {
  const { t } = useT();
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        // Clear the iOS status bar / Dynamic Island so dismissable toasts
        // (incl. error toasts) never land under the pill (viewport-fit=cover).
        top: 'calc(16px + env(safe-area-inset-top, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 99999,
        pointerEvents: 'none',
        maxWidth: 'min(92vw, 480px)',
        width: '100%',
      }}
    >
      {toasts.map((toast) => {
        const { Icon, bg, color } = SEVERITY_STYLE[toast.severity];
        return (
          <div
            key={toast.id}
            role="status"
            tabIndex={0}
            onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDismiss(toast.id); } }}
            onClick={() => onDismiss(toast.id)}
            style={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 12,
              background: bg,
              color: color,
              border: '1px solid currentColor',
              boxShadow: '0 8px 24px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.06)',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.4,
              cursor: 'pointer',
              backdropFilter: 'saturate(140%) blur(6px)',
            }}
          >
            <Icon size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
              {toast.message}
            </div>
            {toast.action && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  try { toast.action.onClick(); } catch (err) { console.warn('[toast] action threw', err); }
                  onDismiss(toast.id);
                }}
                style={{
                  minHeight: 36,
                  padding: '6px 12px',
                  marginTop: -2,
                  border: '1px solid currentColor',
                  borderRadius: 8,
                  background: 'transparent',
                  color: 'inherit',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {toast.action.label}
              </button>
            )}
            <button
              type="button"
              aria-label={t('toast.dismiss')}
              onClick={(e) => { e.stopPropagation(); onDismiss(toast.id); }}
              style={{
                minHeight: 44,
                minWidth: 44,
                marginTop: -8,
                marginRight: -8,
                marginBottom: -8,
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.7,
                padding: 8,
              }}
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe fallback so tests / isolated component renders don't crash.
    // In production, ToastProvider is mounted at the root via main.jsx.
    return {
      showToast: () => null,
      showSuccess: () => null,
      showInfo: () => null,
      showWarning: () => null,
      showError: () => null,
      dismiss: () => {},
    };
  }
  return ctx;
}

export default ToastProvider;
